import React, { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Share2 } from "lucide-react";
import api from "../../api/axios";
import BottomSheet from "../ui/bottom-sheet";
import { getPublicProfileUrl } from "../profile/profile-utils";
import { qrStickerOnWhite } from "../../utils/qrStickerOnWhite";

const MyQrStickerSheet = ({ open, onClose, qrId }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [stickerQrId, setStickerQrId] = useState("");
  const [rawQrCode, setRawQrCode] = useState("");
  const [error, setError] = useState(null);
  const [shareNote, setShareNote] = useState("");

  useEffect(() => {
    if (!open || !qrId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreviewUrl("");
    api
      .get("/qr/me/qr-code")
      .then(async (res) => {
        if (cancelled) return;
        const { qrId: id, qrCode } = res.data || {};
        if (!qrCode) throw new Error("missing image");
        setStickerQrId(id || qrId);
        setRawQrCode(qrCode);
        setPreviewUrl(await qrStickerOnWhite(qrCode));
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load your QR sticker. Try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, qrId]);

  const handleDownload = useCallback(async () => {
    try {
      const src = previewUrl || (rawQrCode ? await qrStickerOnWhite(rawQrCode) : "");
      if (!src) return;
      const link = document.createElement("a");
      link.href = src;
      link.download = `EmergencyQR-${stickerQrId || qrId}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (_) {
      setError("Couldn't download the QR. Try again.");
    }
  }, [previewUrl, rawQrCode, stickerQrId, qrId]);

  const handleShare = useCallback(async () => {
    const url = getPublicProfileUrl(qrId);
    if (navigator.share) {
      try {
        await navigator.share({ title: "Emergency QR", text: `My emergency profile (${qrId})`, url });
      } catch (_) {}
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareNote("Link copied");
      setTimeout(() => setShareNote(""), 2000);
    } catch (_) {}
  }, [qrId]);

  return (
    <BottomSheet open={open} onClose={onClose} title="My QR sticker">
      <div className="my-qr-sheet px-4 pb-4">
        <p className="text-sm text-slate-600 m-0 mb-3">
          Print this sticker or share your public profile link so helpers can reach your emergency info.
        </p>
        {loading && (
          <div className="my-qr-sheet__loading" aria-busy="true">
            <Loader2 size={28} className="animate-spin text-primary" />
          </div>
        )}
        {error && <p className="text-sm text-destructive m-0 mb-3">{error}</p>}
        {previewUrl && !loading && (
          <div className="my-qr-sheet__preview">
            <img src={previewUrl} alt="" className="my-qr-sheet__img" />
            <p className="my-qr-sheet__id font-mono">{stickerQrId || qrId}</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            type="button"
            className="checkout-btn checkout-btn--primary gap-2 justify-center"
            onClick={handleDownload}
            disabled={loading || !previewUrl}
          >
            <Download size={16} aria-hidden />
            Download
          </button>
          <button
            type="button"
            className="checkout-btn checkout-btn--secondary gap-2 justify-center"
            onClick={handleShare}
            disabled={!qrId}
          >
            <Share2 size={16} aria-hidden />
            {shareNote || "Share"}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default MyQrStickerSheet;
